import React, { useState, useEffect } from "react";
import { useAuthContext } from "../../../firebase-files/hooks/useAuthContext";
import { useFirestore } from "../../../firebase-files/hooks/useFirestore";
import { timestamp } from "../../../firebase-config/config";
import RichTextEditor from "react-rte";
import { useDocument } from "../../../firebase-files/hooks/useDocument";
import "./AddNotification.scss";

const AddNotification = ({ onClose }) => {
  const { user } = useAuthContext();
  const { addDocument, response } = useFirestore("notifications");

  const [headingEditor, setHeadingEditor] = useState(
    RichTextEditor.createEmptyValue()
  );
  const [detailsEditor, setDetailsEditor] = useState(
    RichTextEditor.createEmptyValue()
  );
  const [newSelectedOptionContent, setNewSelectedOptionContent] = useState("Offer");

  // Default date = yesterday
  const [notificationDate, setNotificationDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday;
  });

  const [selectedRole, setSelectedRole] = useState("all");
  const [errors, setErrors] = useState({});

  // Time for Offer notifications
  const [fromTime, setFromTime] = useState("09:00");
  const [toTime, setToTime] = useState("18:00");

  const { document: rolesDocument } = useDocument("m_roles", "rolesDoc");

  // Validate form fields
  const validateForm = () => {
    const newErrors = {};

    if (headingEditor.toString("html") === "<p><br></p>") {
      newErrors.heading = "Title is required";
    }

    if (detailsEditor.toString("html") === "<p><br></p>") {
      newErrors.details = "Details are required";
    }

    if (!selectedRole) {
      newErrors.role = "Please select a role";
    }

    if (!notificationDate) {
      newErrors.date = "Please select a date";
    }

    if (newSelectedOptionContent === "Offer") {
      if (!fromTime) newErrors.fromTime = "From time is required";
      if (!toTime) newErrors.toTime = "To time is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleAddTextContent = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    const newNotification = {
      heading: headingEditor.toString("html"),
      notificationType: newSelectedOptionContent,
      fullDetail: detailsEditor.toString("html"),
      status: "active",
      visibleFor: selectedRole,
      tillDate: timestamp.fromDate(new Date(notificationDate)),
      createdAt: timestamp.fromDate(new Date()),
      createdBy: user.uid,
      fromTime: newSelectedOptionContent === "Offer" ? fromTime : null,
      toTime: newSelectedOptionContent === "Offer" ? toTime : null,
    };

    await addDocument(newNotification);

    if (!response.error) {
      setHeadingEditor(RichTextEditor.createEmptyValue());
      setDetailsEditor(RichTextEditor.createEmptyValue());
      setNewSelectedOptionContent("Offer");
      setNotificationDate(() => {
        const yesterday = new Date();
        yesterday.setDate(yesterday.getDate() - 1);
        return yesterday;
      });
      setFromTime("09:00");
      setToTime("18:00");
      onClose();
    }
  };

  // Tomorrow for min date restriction (optional)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    <div className="add-notification-modal">
      <div className="modal-content">
        <div className="d-flex justify-content-center">
          <h5 style={{ color: "var(--heading-color)" }}>
            <b>Add Notification</b>
          </h5>
        </div>
        <hr />
        <div className="notification-tabs">
          {["Offer", "Alert", "Update", "Support"].map((type) => (
            <button
              key={type}
              type="button"
              className={`tab-button ${
                newSelectedOptionContent === type ? "active" : ""
              }`}
              onClick={() => setNewSelectedOptionContent(type)}
            >
              {type}
            </button>
          ))}
        </div>
        <form onSubmit={handleAddTextContent}>
          <div className={`editor-container ${errors.heading ? "error" : ""}`}>
            <RichTextEditor
              value={headingEditor}
              onChange={setHeadingEditor}
              placeholder="Enter notification title..."
              className={errors.heading ? "editor-error" : ""}
            />
            {errors.heading && (
              <div className="error-message">{errors.heading}</div>
            )}
          </div>
          <br />
          <div className={`editor-container ${errors.details ? "error" : ""}`}>
            <RichTextEditor
              value={detailsEditor}
              onChange={setDetailsEditor}
              placeholder="Enter notification details..."
              className={errors.details ? "editor-error" : ""}
            />
            {errors.details && (
              <div className="error-message">{errors.details}</div>
            )}
          </div>
          <br />
          <div className="notification-date-picker">
            <div className={`select-container ${errors.role ? "error" : ""}`}>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
              >
                <option value="" disabled>
                  Select Role
                </option>
                <option value="all">All</option>
                {rolesDocument &&
                  rolesDocument.roles.map((role, index) => (
                    <option
                      key={index}
                      value={role}
                      style={{ textTransform: "capitalize" }}
                    >
                      {role}
                    </option>
                  ))}
              </select>
              {errors.role && (
                <div className="error-message">{errors.role}</div>
              )}
            </div>
            <div className={`date-container ${errors.date ? "error" : ""}`}>
              <input
                type="date"
                min={tomorrow.toISOString().split("T")[0]}
                value={notificationDate.toISOString().substr(0, 10)}
                onChange={(e) => setNotificationDate(new Date(e.target.value))}
              />
              {errors.date && (
                <div className="error-message">{errors.date}</div>
              )}
            </div>
          </div>

          {/* Time picker for Offer notifications */}
          {newSelectedOptionContent === "Offer" && (
            <div className="time-picker-container">
              <div className={`time-field ${errors.fromTime ? "error" : ""}`}>
                <label>From Time:</label>
                <input
                  type="time"
                  value={fromTime}
                  onChange={(e) => setFromTime(e.target.value)}
                  required
                />
                {errors.fromTime && (
                  <div className="error-message">{errors.fromTime}</div>
                )}
              </div>

              <div className={`time-field ${errors.toTime ? "error" : ""}`}>
                <label>To Time:</label>
                <input
                  type="time"
                  value={toTime}
                  onChange={(e) => setToTime(e.target.value)}
                  required
                />
                {errors.toTime && (
                  <div className="error-message">{errors.toTime}</div>
                )}
              </div>
            </div>
          )}

          <div className="d-flex justify-content-between">
            <button type="button" className="btn_border" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn_fill">
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddNotification;
